// Keep in sync with the BE's GENERIC_COMPANY_NAME_TOKENS: words too generic
// to identify a company on their own. A single-token customer name in this
// set never containment-matches a multi-word employer (see
// companyNamesFuzzyMatch). It can still match another single-token name
// exactly.
const GENERIC_COMPANY_NAME_TOKENS = new Set([
  "american",
  // Common surnames: "Smith & Company" must not swallow every
  // "Smith <anything>" employer — a shared surname alone is not identity.
  "brown",
  "davis",
  "johnson",
  "jones",
  "miller",
  "smith",
  "williams",
  "wilson",
  "group",
  "industrial",
  "industries",
  "logistics",
  "manufacturing",
  "national",
  "plant",
  "premier",
  "products",
  "quality",
  "services",
  "solutions",
  "systems",
  "united",
]);

// Mirrors the BE's normalizeCompanyName exactly: lowercased, punctuation and
// legal suffixes stripped, whitespace collapsed. A resume's "Acme
// Manufacturing, LLC" matches a customer row named "Acme Manufacturing".
const normalizeName = (name: string): string =>
  name
    .toLowerCase()
    // Collapse dotted acronyms ("A.M.C.A.", "U.S Steel" — trailing dot
    // optional) into a single token before dots become spaces.
    .replace(/\b[a-z](?:\.[a-z])+\.?/g, (m) => m.replace(/\./g, ""))
    // Possessives glue to their token ("smith's" -> "smiths") instead of
    // splitting, so a customer named bare "Smith" doesn't containment-match
    // every "Smith's ..." employer — a possessive is usually a different
    // entity than the bare name.
    .replace(/['’]s\b/g, "s")
    // Hyphens are plain punctuation: "Mercedes-Benz", "Mercedes - Benz", and
    // "AMCA - Advanced ..." all flatten to one token list. No alias handling.
    .replace(/[.,'’&-]/g, " ")
    .replace(
      // Legal suffixes plus connector/filler words that carry no identity.
      /\b(inc|incorporated|llc|llp|ltd|limited|corp|corporation|co|company|and|the|of)\b/g,
      " ",
    )
    .replace(/\s+/g, " ")
    .trim();

export const COMPANY_MATCH_REASONS = {
  CURRENT_CUSTOMER: "Currently works at a Laborup company",
  CURRENT_PLACEMENT: "Current Laborup placement -- placed > 6 months ago",
  OTHER: "Worked at another Laborup company",
  SAME_COMPANY: "Worked at this company previously",
} as const;

export type CompanyMatchReason =
  (typeof COMPANY_MATCH_REASONS)[keyof typeof COMPANY_MATCH_REASONS];

// A hit against the customer list. `company` is the customer, `matched` is the
// name that actually matched — the same string, unless the candidate's
// employer matched one of the customer's related company names.
export interface CompanyNameMatch {
  company: string;
  matched: string;
}

// Normalized name -> the customer that name belongs to.
export type CompanyNameMap = Map<string, CompanyNameMatch>;

export const companies = {
  /**
   * Build a lookup of normalized name -> the customer it belongs to, so
   * free-text work-experience companies can be matched
   * case/punctuation/suffix-insensitively.
   *
   * A customer is known by more than its record name: an abbreviation ("MCM"
   * for Metal Company Manufacturing), a legal name, a sister company. Those
   * live in its internal config as relatedCompanyNames and get their own entry
   * here, pointing at the same customer. `matched` is the name that was hit,
   * so a banner can say which one caught it. Mirrors guac's
   * findCustomerEmployerMatch.
   */
  buildCompanyNameMap: (
    companyList: {
      name: string;
      companyInternalConfig?: { relatedCompanyNames?: string[] } | null;
    }[],
  ): CompanyNameMap => {
    const map: CompanyNameMap = new Map();
    // Two passes so a customer's own name always wins. A sister company can
    // itself be a customer, and the parent will list it as a related name: in
    // one pass whichever company came first would claim the key, and the
    // sister's own jobs would stop reading as the same company.
    for (const company of companyList) {
      const normalized = normalizeName(company.name);
      if (normalized && !map.has(normalized)) {
        map.set(normalized, { company: company.name, matched: company.name });
      }
    }
    for (const company of companyList) {
      for (const matched of company.companyInternalConfig
        ?.relatedCompanyNames ?? []) {
        const normalized = normalizeName(matched);
        if (normalized && !map.has(normalized)) {
          map.set(normalized, { company: company.name, matched });
        }
      }
    }
    return map;
  },

  /**
   * Distinct employer names from a candidate's work history, in order, deduped
   * case-insensitively. Canonical resolved name first, truthy fallback to free
   * text for roles nova hasn't resolved to an internal company.
   */
  extractEmployerNames: (
    workExperiences: {
      company?: string | null;
      internalCompany?: { name?: string | null } | null;
    }[],
  ): string[] => {
    const seen = new Set<string>();
    const names: string[] = [];
    for (const we of workExperiences) {
      // Truthy fallback (not ??) matches BE: whitespace-only resolved name
      // still falls through to the free-text company.
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const name = we.internalCompany?.name?.trim() || we.company?.trim() || "";
      if (name && !seen.has(name.toLowerCase())) {
        seen.add(name.toLowerCase());
        names.push(name);
      }
    }
    return names;
  },

  /**
   * Normalized token list for a company name.
   */
  companyNameTokens: (name: string): string[] =>
    normalizeName(name).split(" ").filter(Boolean),

  /**
   * True when two normalized name tokens should be considered the same:
   * exact match, or typo tolerance scaled to token length. Edit distance
   * <= 1 when both tokens are 5+ characters, <= 2 when both are 10+
   * (two errors in "manufacturing" is still unmistakably the same word,
   * two errors in a 6-letter name is a different name). Tokens under 5
   * characters require exactness so "cintas" never matches "cintel".
   */
  companyTokensMatch: (a: string, b: string): boolean => {
    if (a === b) return true;
    if (a.length < 5 || b.length < 5) return false;
    const maxEdits = a.length >= 10 && b.length >= 10 ? 2 : 1;
    const [short, long] = a.length <= b.length ? [a, b] : [b, a];
    if (long.length - short.length > maxEdits) return false;
    // Banded Levenshtein: only cells within maxEdits of the diagonal can
    // stay under the cap, so wider cells are pinned to maxEdits + 1.
    let prev = Array.from({ length: short.length + 1 }, (_, k) => k);
    for (let j = 1; j <= long.length; j++) {
      const curr = new Array<number>(short.length + 1);
      curr[0] = j;
      let rowMin = curr[0];
      for (let i = 1; i <= short.length; i++) {
        if (Math.abs(i - j) > maxEdits) {
          curr[i] = maxEdits + 1;
        } else {
          curr[i] = Math.min(
            prev[i] + 1,
            curr[i - 1] + 1,
            prev[i - 1] + (short[i - 1] === long[j - 1] ? 0 : 1),
          );
        }
        if (curr[i] < rowMin) rowMin = curr[i];
      }
      if (rowMin > maxEdits) return false;
      prev = curr;
    }
    return prev[short.length] <= maxEdits;
  },

  /**
   * A hit when either name's token set is contained in the other's (so "Acme
   * Manufacturing - Knoxville Plant" matches customer "Acme Manufacturing"),
   * with per-token typo tolerance via companyTokensMatch. Containment by a
   * single-token name is riskier — a customer named just "Premier" must not
   * swallow every multi-word employer containing that word — so it only
   * counts when the token is distinctive: 4+ characters (so acronyms like
   * "AMCA" and short brands like "Ford" still flag, but 3-letter tokens
   * don't), not a generic industry word, and matched exactly (no typo
   * tolerance). The cost
   * asymmetry here favors matching aggressively (a false positive skips one
   * candidate, a false negative texts a customer's employee), hence
   * containment over full equality.
   */
  companyNamesFuzzyMatch: (aTokens: string[], bTokens: string[]): boolean => {
    if (aTokens.length === 0 || bTokens.length === 0) return false;
    const [small, big] =
      aTokens.length <= bTokens.length
        ? [aTokens, bTokens]
        : [bTokens, aTokens];
    if (small.length === 1 && big.length > 1) {
      const token = small[0];
      return (
        token.length >= 4 &&
        !GENERIC_COMPANY_NAME_TOKENS.has(token) &&
        big.includes(token)
      );
    }
    return small.every((s) =>
      big.some((b) => companies.companyTokensMatch(s, b)),
    );
  },

  /**
   * Fuzzy token match against the customer map, mirroring the BE's
   * companyNamesFuzzyMatch semantics so FE banner and BE sourcing exclusion
   * can't drift. Returns the customer and the name that matched, which differ
   * when the hit came through a related company name.
   */
  findCompanyMatch: (
    companyNameMap: CompanyNameMap,
    rawName: string,
  ): CompanyNameMatch | undefined => {
    const normalized = normalizeName(rawName);
    if (!normalized) return undefined;
    // An exact normalized hit is unambiguous, so it beats the scan below. A
    // single-token name matches by containment, which would otherwise let a
    // customer called "Acme" resolve to "Acme Manufacturing" purely because
    // that one sits earlier in the map.
    const exact = companyNameMap.get(normalized);
    if (exact) return exact;

    const nameTokens = normalized.split(" ").filter(Boolean);
    for (const [key, entry] of companyNameMap) {
      const keyTokens = key.split(" ").filter(Boolean);
      if (companies.companyNamesFuzzyMatch(nameTokens, keyTokens)) return entry;
    }
    return undefined;
  },

  /**
   * Return the display name of the first company in the map matching any of
   * the given free-text company names, or undefined when none match.
   * Rank is for sorting on highest priority to show
   *
   */
  findAllCompanyMatches: (
    companyNameMap: CompanyNameMap,
    names: (string | null | undefined)[],
    internalCompany?: string,
  ):
    | (CompanyNameMatch & { reason: CompanyMatchReason; rank: number })[]
    | null => {
    const matches = names
      .map((c, index) => {
        if (!c) return undefined;
        const match = companies.findCompanyMatch(companyNameMap, c);
        if (!match) return undefined;
        // names[0] is the candidate's current/most recent employer (callers
        // pass work history recency-first) — actively poaching a customer's
        // employee is the most severe case, so it outranks everything.
        if (index === 0) {
          return {
            ...match,
            reason: COMPANY_MATCH_REASONS.CURRENT_CUSTOMER,
            rank: 3,
          };
        }
        if (internalCompany && match.company === internalCompany) {
          return {
            ...match,
            reason: COMPANY_MATCH_REASONS.SAME_COMPANY,
            rank: 2,
          };
        }

        return { ...match, reason: COMPANY_MATCH_REASONS.OTHER, rank: 1 };
      })
      .filter((m) => m !== undefined)
      .sort((a, b) => b.rank - a.rank);

    return matches.length > 0 ? matches : null;
  },
};
